// §patient-address (FE_PRD_INTAKE_PATIENT_ADDRESS_UPDATE §1.1 + §1.2(b)). Follow-ups
// collect no shipping address: the BE inherits it from the parent visit / patient
// row. Beluga treats every new visit as overriding the patient-profile address,
// so a patient who moved since the parent visit would silently have the OLD
// address re-sent. The follow-up checkout therefore shows the address it is about
// to use and lets the patient change it.
//
// Two write paths (chosen in components/followup/useFollowupShipTo.js):
//   • With a dashboard session token (lib/sessionBridge): POST
//     /beluga/update-patient-address before payment. Beluga, the users row and
//     the history table are all updated, and the submit carries no address.
//   • Without one (fallback): the change goes out as contact.{address,city,state,
//     zip} on /followup/submit. An explicit contact address wins over every BE
//     inheritance tier and reaches Beluga on the visit, but writes no history row
//     and leaves the users row alone. The next follow-up still inherits it,
//     because it chains off this visit (Tier 1).
//
// Pure helpers only, relative imports, so Jest can load them directly.
import { US_STATES } from '../data/usStates';

const STATE_CODES = new Set(US_STATES.map((s) => s.value).filter(Boolean));

export const SHIP_TO_LIMITS = { address: 200, city: 100 };

const firstString = (...vals) => {
  for (const v of vals) if (typeof v === 'string' && v.trim()) return v.trim();
  return '';
};

// The address the lookup says is on file, or null when it carries no street
// line. The /followup/lookup-user shape is not pinned for address fields (only
// `state` is relied on elsewhere), so read the likely spellings. Beluga's
// "address" is a single line with apt/unit included, so address2 is folded in.
export function shipToFromLookup(user) {
  if (!user || typeof user !== 'object') return null;
  const line1 = firstString(user.address, user.address1, user.user_address, user.street);
  const line2 = firstString(user.address2, user.user_address2);
  const address = line1 && line2 ? `${line1} ${line2}` : line1;
  if (!address) return null;
  return {
    address,
    city: firstString(user.city, user.user_city),
    state: firstString(user.state, user.user_state).toUpperCase(),
    zip: firstString(user.zip, user.zipCode, user.zip_code, user.user_zip, user.user_zipcode),
  };
}

// Trim, uppercase the state, collapse whitespace (single line) and truncate
// ZIP+4 to 5 digits, the shape the BE/Beluga store.
export function normalizeShipTo(v) {
  const clean = (s) => String(s || '').replace(/\s+/g, ' ').trim();
  const zip = clean(v && v.zip);
  return {
    address: clean(v && v.address),
    city: clean(v && v.city),
    state: clean(v && v.state).toUpperCase(),
    zip: /^\d{5}-\d{4}$/.test(zip) ? zip.slice(0, 5) : zip,
  };
}

// Field → message map, empty when valid. Mirrors the BE's 400 rules (PRD §2):
// address ≤200 single line, city ≤100, 2-letter state, 5-digit zip (ZIP+4 ok).
// Validate the RAW value so a pasted newline is caught before normalizing.
export function validateShipTo(v) {
  const errors = {};
  const raw = v || {};
  const n = normalizeShipTo(raw);
  if (!n.address) errors.address = 'Street address is required';
  else if (/[\r\n]/.test(String(raw.address))) errors.address = 'Enter the address on one line (include apt/unit)';
  else if (n.address.length > SHIP_TO_LIMITS.address) errors.address = `Address must be ${SHIP_TO_LIMITS.address} characters or fewer`;
  if (!n.city) errors.city = 'City is required';
  else if (n.city.length > SHIP_TO_LIMITS.city) errors.city = `City must be ${SHIP_TO_LIMITS.city} characters or fewer`;
  if (!STATE_CODES.has(n.state)) errors.state = 'Select a state';
  if (!n.zip) errors.zip = 'ZIP code is required';
  else if (!/^\d{5}(-\d{4})?$/.test(n.zip)) errors.zip = 'Enter a 5-digit ZIP code';
  return errors;
}

// Fields to spread into the follow-up `contact`. Only a patient-confirmed change
// is sent; no change → {} so the BE's inheritance (incl. Tier 0) stays in charge.
export function shipToContactFields(changed) {
  if (!changed) return {};
  const n = normalizeShipTo(changed);
  return { address: n.address, city: n.city, state: n.state, zip: n.zip };
}

// How the Ship-to form reacts to a failed POST /beluga/update-patient-address
// (PRD §2). Nothing changed on any of these, so the form stays open, except
// 'fallback': the route is unmounted (PR not merged) or the masterId isn't this
// patient's, so the change goes out in the submit `contact` instead.
//   { kind: 'field', field, message } | { kind: 'form', message } | { kind: 'fallback' }
export function classifyAddressSaveError(error) {
  const res = error && error.response;
  if (!res) return { kind: 'form', message: "Couldn't save your address. Check your connection and try again." };
  const data = res.data || {};
  if (res.status === 404) return { kind: 'fallback' };
  if (res.status === 400 && data.code === 'validation_error' && ['address', 'city', 'state', 'zip'].includes(data.field)) {
    return { kind: 'field', field: data.field, message: data.message || 'Check this field' };
  }
  if (res.status === 422 && data.message) return { kind: 'form', message: data.message };
  if (res.status === 502) return { kind: 'form', message: "Couldn't reach the pharmacy partner. Try again." };
  return { kind: 'form', message: data.message || "Couldn't save your address. Try again." };
}
